import * as XLSX from "xlsx";
import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Users,
  Phone,
  CalendarDays,
  Eye,
  X,
  MapPinned,
  Clock3,
  RefreshCw,
  ChevronDown,
  Download,
} from "lucide-react";

import { api } from "@/lib/api";

export default function PlanVisitsPage() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [expandedDates, setExpandedDates] = useState({});
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitDetail, setVisitDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadVisits();
  }, []);

  const loadVisits = async () => {
    try {
      setRefreshing(true);
      setError("");

      const response = await api.get("/plan-visits");

      const data = response.data?.visits || [];

      setVisits(data);

      // Abrir inicialmente la fecha más reciente
      if (data.length) {
        const latestDate = getDateKey(data[0].created_at);

        setExpandedDates((previous) => ({
          ...previous,
          [latestDate]: true,
        }));
      }
    } catch (err) {
      console.error("Error cargando visitas:", err);

      setError(
        err?.response?.data?.detail ||
          "No fue posible cargar el registro de visitas."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const parseServerDate = (value) => {
  if (!value) return null;

  const stringValue = String(value);

  // El backend guarda las fechas en UTC.
  // Si no viene indicada la zona horaria, la tratamos como UTC.
  if (
    !stringValue.endsWith("Z") &&
    !/[+-]\d{2}:\d{2}$/.test(stringValue)
  ) {
    return new Date(`${stringValue}Z`);
  }

  return new Date(stringValue);
};

const getDateKey = (value) => {
  const date = parseServerDate(value);

  if (!date || Number.isNaN(date.getTime())) {
    return "sin-fecha";
  }

  return date.toLocaleDateString("en-CA", {
    timeZone: "America/La_Paz",
  });
};

const formatDate = (value) => {
  const date = parseServerDate(value);

  if (!date || Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("es-BO", {
    timeZone: "America/La_Paz",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  const formatDay = (dateKey) => {
    if (!dateKey || dateKey === "sin-fecha") {
      return "Fecha no disponible";
    }

    const date = new Date(`${dateKey}T12:00:00`);

    return date.toLocaleDateString("es-BO", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const isToday = (dateKey) => {
    const today = new Date().toLocaleDateString("en-CA", {
      timeZone: "America/La_Paz",
    });

    return dateKey === today;
  };

  const filteredVisits = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return visits;

    return visits.filter((visit) => {
      const nombre = String(visit.nombre || "").toLowerCase();
      const telefono = String(visit.telefono || "").toLowerCase();
      const origen = String(visit.origen || "").toLowerCase();

      return (
        nombre.includes(term) ||
        telefono.includes(term) ||
        origen.includes(term)
      );
    });
  }, [visits, search]);

  const groupedVisits = useMemo(() => {
    const groups = {};

    filteredVisits.forEach((visit) => {
      const key = getDateKey(visit.created_at);

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(visit);
    });

    return Object.entries(groups).sort(
      ([dateA], [dateB]) => dateB.localeCompare(dateA)
    );
  }, [filteredVisits]);

  const uniqueVisitors = useMemo(() => {
    const phones = new Set();

    visits.forEach((visit) => {
      if (visit.telefono) {
        phones.add(String(visit.telefono).replace(/\D/g, ""));
      }
    });

    return phones.size;
  }, [visits]);

  const todayVisits = useMemo(() => {
    return visits.filter((visit) =>
      isToday(getDateKey(visit.created_at))
    ).length;
  }, [visits]);

  const toggleDate = (dateKey) => {
    setExpandedDates((previous) => ({
      ...previous,
      [dateKey]: !previous[dateKey],
    }));
  };

  const openVisit = async (visit) => {
    setSelectedVisit(visit);
    setVisitDetail(null);
    setDetailLoading(true);

    try {
      const response = await api.get(`/plan-visits/${visit.id}`);

      setVisitDetail(response.data);
    } catch (err) {
      console.error("Error cargando detalle de visita:", err);

      setVisitDetail({
        visit,
        interests: [],
        error:
          err?.response?.data?.detail ||
          "No fue posible cargar el detalle de la visita.",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeVisit = () => {
    setSelectedVisit(null);
    setVisitDetail(null);
  };

  const exportCRMContacts = () => {
    const contacts = new Map();

    for (const visit of visits) {
      const name = String(visit.nombre || "").trim();
      const rawPhone = String(visit.telefono || "").trim();

      if (!name || !rawPhone) continue;

      const digits = rawPhone.replace(/\D/g, "");
      let phone = "";

      if (digits.length === 8) {
        phone = `+591${digits}`;
      } else if (digits.length === 11 && digits.startsWith("591")) {
        phone = `+${digits}`;
      } else if (
        rawPhone.startsWith("+") &&
        digits.length >= 10 &&
        digits.length <= 15
      ) {
        phone = `+${digits}`;
      } else {
        continue;
      }

      if (!contacts.has(phone)) {
        contacts.set(phone, { name, phone });
      }
    }

    if (contacts.size === 0) {
      window.alert("No hay contactos válidos para exportar.");
      return;
    }

    const rows = [
      ["name", "phone"],
      ...Array.from(contacts.values(), ({ name, phone }) => [name, phone]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Forzar los teléfonos como texto para conservar +591.
    for (let row = 1; row < rows.length; row++) {
      const cell = XLSX.utils.encode_cell({ r: row, c: 1 });
      worksheet[cell] = {
        t: "s",
        v: String(rows[row][1]),
      };
    }

    worksheet["!cols"] = [
      { wch: 35 },
      { wch: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contactos");

    const date = new Date().toLocaleDateString("en-CA", {
      timeZone: "America/La_Paz",
    });

    XLSX.writeFile(workbook, `contactos_PSC_CRM_${date}.xlsx`);

    window.alert(
      `Exportación completada: ${contacts.size} contactos únicos.`
    );
  };

  return (
    <div className="plan-visits-page">
      {/* HEADER */}
      <div className="plan-visits-header">
        <div>
          <div className="plan-visits-eyebrow">
            <MapPinned size={15} />
            VISOR PÚBLICO
          </div>

          <h1>Registro de Visitas</h1>

          <p>
            Seguimiento de las personas que ingresaron al visor
            interactivo del proyecto.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="plan-visits-refresh"
            onClick={exportCRMContacts}
            disabled={loading || visits.length === 0}
          >
            <Download size={17} />
            Exportar contactos CRM
          </button>

          <button
            type="button"
            className="plan-visits-refresh"
            onClick={loadVisits}
            disabled={refreshing}
          >
            <RefreshCw
              size={17}
              className={refreshing ? "spin" : ""}
            />
            Actualizar
          </button>
        </div>
      </div>

      {/* RESUMEN */}
      <div className="plan-visits-summary">
        <div className="plan-visits-summary-card">
          <div className="summary-icon summary-icon-primary">
            <Users size={22} />
          </div>

          <div>
            <span>Visitas registradas</span>
            <strong>{visits.length}</strong>
            <small>Ingresos al visor</small>
          </div>
        </div>

        <div className="plan-visits-summary-card">
          <div className="summary-icon summary-icon-blue">
            <Phone size={22} />
          </div>

          <div>
            <span>Visitantes</span>
            <strong>{uniqueVisitors}</strong>
            <small>Celulares registrados</small>
          </div>
        </div>

        <div className="plan-visits-summary-card">
          <div className="summary-icon summary-icon-green">
            <CalendarDays size={22} />
          </div>

          <div>
            <span>Visitas hoy</span>
            <strong>{todayVisits}</strong>
            <small>Actividad de hoy</small>
          </div>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="plan-visits-panel">
        <div className="plan-visits-panel-header">
          <div>
            <h2>Actividad del visor</h2>
            <p>
              Consulta los visitantes y revisa los lotes que
              hayan despertado su interés.
            </p>
          </div>

          <div className="plan-visits-search">
            <Search size={18} />

            <input
              type="text"
              placeholder="Buscar nombre o celular..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Limpiar búsqueda"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="plan-visits-empty">
            <div className="plan-visits-loader" />
            <span>Cargando registro de visitas...</span>
          </div>
        )}

        {!loading && error && (
          <div className="plan-visits-error">
            {error}
          </div>
        )}

        {!loading && !error && groupedVisits.length === 0 && (
          <div className="plan-visits-empty">
            <Users size={38} />
            <strong>No hay visitas para mostrar</strong>
            <span>
              {search
                ? "No encontramos resultados para esa búsqueda."
                : "Todavía no existen registros de visitantes."}
            </span>
          </div>
        )}

        {!loading &&
          !error &&
          groupedVisits.map(([dateKey, dateVisits]) => {
            const expanded = expandedDates[dateKey];

            return (
              <section
                className="plan-visits-day"
                key={dateKey}
              >
                <button
                  type="button"
                  className="plan-visits-day-header"
                  onClick={() => toggleDate(dateKey)}
                >
                  <div className="day-header-left">
                    <div className="day-calendar">
                      <CalendarDays size={20} />
                    </div>

                    <div>
                      <strong>
                        {formatDay(dateKey)}
                      </strong>

                      <span>
                        {dateVisits.length}{" "}
                        {dateVisits.length === 1
                          ? "visita"
                          : "visitas"}
                      </span>
                    </div>
                  </div>

                  <div className="day-header-right">
                    {isToday(dateKey) && (
                      <span className="today-badge">
                        HOY
                      </span>
                    )}

                    <ChevronDown
                      size={20}
                      className={
                        expanded
                          ? "chevron-open"
                          : ""
                      }
                    />
                  </div>
                </button>

                {expanded && (
                  <div className="plan-visits-list">
                    {dateVisits.map((visit) => (
                      <article
                        className="plan-visit-row"
                        key={visit.id}
                      >
                        <div className="visit-time">
  <Clock3 size={16} />
  <span>
    {parseServerDate(
      visit.created_at
    ).toLocaleTimeString("es-BO", {
      timeZone: "America/La_Paz",
      hour: "2-digit",
      minute: "2-digit",
    })}
  </span>
</div>

                        <div className="visit-person">
                          <div className="visit-avatar">
                            {(
                              visit.nombre || "V"
                            )
                              .trim()
                              .charAt(0)
                              .toUpperCase()}
                          </div>

                          <div>
                            <strong>
                              {visit.nombre || "Sin nombre"}
                            </strong>

                            <span>
                              <Phone size={13} />
                              {visit.telefono || "Sin teléfono"}
                            </span>
                          </div>
                        </div>

                        <div className="visit-origin">
                          <span>
                            Visor plano
                          </span>
                        </div>

                        <div className="visit-action">
                          <button
                            type="button"
                            className="visit-view-button"
                            onClick={() =>
                              openVisit(visit)
                            }
                          >
                            <Eye size={17} />
                            Ver detalle
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
      </div>

      {/* MODAL DETALLE */}
      {selectedVisit && (
        <div
          className="plan-visits-modal-backdrop"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget
            ) {
              closeVisit();
            }
          }}
        >
          <aside className="plan-visits-modal">
            <div className="plan-visits-modal-header">
              <div>
                <span>DETALLE DE VISITA</span>
                <h2>
                  {selectedVisit.nombre ||
                    "Visitante"}
                </h2>
              </div>

              <button
                type="button"
                className="plan-visits-modal-close"
                onClick={closeVisit}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>

            {detailLoading ? (
              <div className="plan-visits-modal-loading">
                <div className="plan-visits-loader" />
                <span>
                  Cargando información...
                </span>
              </div>
            ) : (
              <>
                <div className="visit-detail-contact">
                  <div className="detail-contact-icon">
                    <Phone size={20} />
                  </div>

                  <div>
                    <span>Celular</span>
                    <strong>
                      {selectedVisit.telefono ||
                        "—"}
                    </strong>
                  </div>
                </div>

                <div className="visit-detail-grid">
                  <div>
                    <span>Fecha y hora</span>
                    <strong>
                      {formatDate(
                        selectedVisit.created_at
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Origen</span>
                    <strong>
                      Visor plano
                    </strong>
                  </div>

                  <div>
                    <span>ID de visita</span>
                    <strong className="detail-id">
                      {selectedVisit.id}
                    </strong>
                  </div>
                </div>

                <div className="visit-interest-section">
                  <div className="interest-section-title">
                    <div>
                      <MapPinned size={19} />
                    </div>

                    <div>
                      <strong>
                        Lotes consultados
                      </strong>

                      <span>
                        Intereses registrados durante
                        esta visita
                      </span>
                    </div>
                  </div>

                  {visitDetail?.interests?.length ? (
                    <div className="interest-list">
                      {visitDetail.interests.map(
                        (interest) => (
                          <div
                            className="interest-card"
                            key={interest.id}
                          >
                            <div className="interest-number">
                              {interest.lote}
                            </div>

                            <div>
                              <strong>
                                Manzano{" "}
                                {interest.manzano}
                              </strong>

                              <span>
                                Lote{" "}
                                {interest.lote}
                              </span>
                            </div>

                            <small>
                              {formatDate(
                                interest.created_at
                              )}
                            </small>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="no-interests">
                      <MapPinned size={22} />

                      <div>
                        <strong>
                          Sin lotes consultados
                        </strong>

                        <span>
                          Esta visita todavía no
                          registra intereses sobre
                          lotes.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}